import React, { useState } from "react";
import { authFetch, api } from "../utils/api";
import { useApp } from "../context/AppContext";
import { t } from "../utils/i18n";
import { useNavigate, useSearchParams } from "react-router-dom";
import { compressImage } from "../utils/imageCompress";
import { TOUS_RITUAL, RITUAL_GROUPS, CategoryGroup, TOUS_PRO_EVENTS, PRO_EVENT_GROUPS } from "../utils/taxonomy";
import { tTax } from "../utils/taxonomyI18n";
import LiveRecordModal from "../components/LiveRecordModal";
import CatalogPicker from "../components/CatalogPicker";
import VenueField from "../components/VenueField";
import AudienceSelector from "../components/AudienceSelector";
import FriendSelector from "../components/FriendSelector";
import PublishAsSheet, { PublishIdentity } from "../components/PublishAsSheet";
import { ChevronDown } from "lucide-react";
import { CameraIcon } from "../components/messages/icons";
import { MOVE_MODE_ICONS, MoveMode } from "../components/move/MoveIcons";
import CarMakeField from "../components/move/CarMakeField";
import ImageCropModal from "../components/ImageCropModal";

// ── Données ──────────────────────────────────────────────────────────────────
const TOUS_COMMUNITY = ["Art & Créativité","Bien-être","Business & Entrepreneuriat","Cinéma & Séries","Cuisine & Gastronomie","Développement personnel","Écologie","Famille","Gaming","High-Tech","Langues","Littérature & Lecture","Mode & Beauté","Musique","Photo & Vidéo","Sciences","Sport","Technologie","Voyage & Aventure","Libre"].sort();
// Libellés résolus via i18n au rendu (t(lang, ...)) — les clés brutes restent la valeur stockée
// / envoyée au serveur (frequence, selectedDays "Lun".."Dim", audience), jamais traduites.
const FREQUENCES = [
  { key:"daily", labelKey:"cr_freq_daily" },
  { key:"weekly", labelKey:"cr_freq_weekly" },
  { key:"biweekly", labelKey:"cr_freq_biweekly" },
  { key:"monthly", labelKey:"cr_freq_monthly" },
];
// Empêche de sélectionner une date déjà passée dans tout sélecteur <input type="date"> de cette
// page — aucun événement ne devrait pouvoir être créé "hier". Recalculé au montage du module,
// largement suffisant pour la durée d'une session (un rechargement de page suffit sinon).
const todayISO = new Date().toISOString().slice(0, 10);
const JOURS = [
  { v:"Lun", k:"cr_day_mon" }, { v:"Mar", k:"cr_day_tue" }, { v:"Mer", k:"cr_day_wed" },
  { v:"Jeu", k:"cr_day_thu" }, { v:"Ven", k:"cr_day_fri" }, { v:"Sam", k:"cr_day_sat" },
  { v:"Dim", k:"cr_day_sun" },
];
const DOMAINS = [
  { key:"passion", label:"PASSION", descKey:"cr_dom_passion_desc" },
  { key:"power", label:"POWER", descKey:"cr_dom_power_desc" },
  { key:"ritual", label:"RITUAL", descKey:"cr_dom_ritual_desc" },
  { key:"community", label:"COMMUNITY", descKey:"cr_dom_community_desc" },
  { key:"feedback", label:"FEEDBACK", descKey:"cr_dom_feedback_desc" },
  { key:"move", label:"MOVE", descKey:"cr_dom_move_desc" },
];
const MOVE_MODES: { key: MoveMode; labelKey: string }[] = [
  { key: "ride", labelKey: "move_mode_ride" },
  { key: "bike", labelKey: "move_mode_bike" },
  { key: "walk", labelKey: "move_mode_walk" },
  { key: "scoot", labelKey: "move_mode_scoot" },
];
const AUDIENCE_KEYS = [
  { key:"public", labelKey:"cr_aud_public", subKey:"cr_aud_public_sub" },
  { key:"amis", labelKey:"cr_aud_friends", subKey:"cr_aud_friends_sub" },
  // "Sur invitation" n'a pas de sens pour un feedback : simple retour d'expérience, pas
  // d'événement à rejoindre — FEEDBACK_AUDIENCES ci-dessous l'exclut.
  { key:"prive", labelKey:"cr_aud_invite", subKey:"cr_aud_invite_sub" },
];

// ── Icons ─────────────────────────────────────────────────────────────────────

// ── Composants ────────────────────────────────────────────────────────────────
// groups (optionnel) : regroupe la liste par famille (Sport, Culture, Sciences...) tant que
// l'utilisateur ne tape rien — dès qu'il cherche, on repasse en liste plate filtrée, plus
// rapide pour retrouver un mot précis dans une taxonomie devenue large.
function CategoryDropdown({ cats, groups, selected, onSelect, placeholder, lang }: { cats: string[]; groups?: CategoryGroup[]; selected: string; onSelect: (c: string) => void; placeholder: string; lang: string }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const filtered = cats.filter(c => c.toLowerCase().includes(search.toLowerCase()));
  const row = (cat: string) => (
    <div key={cat} onClick={() => { onSelect(cat); setOpen(false); setSearch(""); }} style={{ padding: "11px 14px", cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "space-between", backgroundColor: selected === cat ? "#F0F0F0" : "transparent", color: "#0a0a0a", fontWeight: selected === cat ? 600 : 400 }}>
      {tTax(lang, cat)}
      {selected === cat && <span style={{ color: "#0a0a0a", fontWeight: 700 }}>✓</span>}
    </div>
  );
  return (
    <div style={{ position: "relative", marginBottom: 16 }}>
      <button onClick={() => setOpen(!open)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", borderRadius: 10, border: `1px solid ${selected ? "#0a0a0a" : "#E0E0E0"}`, backgroundColor: selected ? "white" : "#F8F8F8", cursor: "pointer", fontSize: 14, color: selected ? "#0a0a0a" : "#999" }}>
        <span>{selected ? tTax(lang, selected) : placeholder}</span>
        <span style={{ color: "#999" }}>⌄</span>
      </button>
      {open && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, backgroundColor: "white", border: "1px solid #E0E0E0", borderRadius: 12, zIndex: 100, maxHeight: 340, overflow: "hidden", boxShadow: "0 8px 30px rgba(0,0,0,0.15)", marginTop: 4 }}>
          <div style={{ padding: "10px 12px", borderBottom: "1px solid #F0F0F0" }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t(lang, "search")} autoFocus style={{ border: "none", outline: "none", fontSize: 14, width: "100%" }} />
          </div>
          <div style={{ overflowY: "auto", maxHeight: 280 }}>
            {groups && !search ? groups.map(g => (
              <div key={g.family}>
                <div style={{ position: "sticky", top: 0, padding: "7px 14px", fontSize: 10.5, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase" as const, color: "#999", backgroundColor: "#FAFAFA", borderBottom: "1px solid #F5F5F5" }}>{tTax(lang, g.family)}</div>
                {g.items.map(row)}
              </div>
            )) : filtered.map(row)}
          </div>
        </div>
      )}
    </div>
  );
}

// Variante de CategoryDropdown pour passion/power : même chrome (bouton + panneau), mais le
// contenu vient du catalogue partagé en base (DB-backed, growable) via CatalogPicker au lieu
// des listes statiques — recherche, groupement par catégorie et proposition d'un nouveau
// passion/power inclus.
function CatalogCategoryDropdown({ catalogType, selected, onSelect, placeholder, lang }: { catalogType: "passion" | "power"; selected: string; onSelect: (c: string) => void; placeholder: string; lang: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: "relative", marginBottom: 16 }}>
      <button onClick={() => setOpen(!open)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", borderRadius: 10, border: `1px solid ${selected ? "#0a0a0a" : "#E0E0E0"}`, backgroundColor: selected ? "white" : "#F8F8F8", cursor: "pointer", fontSize: 14, color: selected ? "#0a0a0a" : "#999" }}>
        <span>{selected ? tTax(lang, selected) : placeholder}</span>
        <span style={{ color: "#999" }}>⌄</span>
      </button>
      {open && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, backgroundColor: "white", border: "1px solid #E0E0E0", borderRadius: 12, zIndex: 100, overflow: "hidden", boxShadow: "0 8px 30px rgba(0,0,0,0.15)", marginTop: 4 }}>
          <CatalogPicker
            type={catalogType}
            mode="single"
            selected={selected ? [selected] : []}
            onSelect={c => { onSelect(c); setOpen(false); }}
            onClose={() => setOpen(false)}
          />
        </div>
      )}
    </div>
  );
}

function Toggle({ value, onChange, label, sub }: { value: boolean; onChange: (v: boolean) => void; label: string; sub?: string }) {
  return (
    <div onClick={() => onChange(!value)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 12, border: "1px solid #E0E0E0", cursor: "pointer", marginBottom: 12 }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>{sub}</div>}
      </div>
      <div style={{ width: 44, height: 24, borderRadius: 12, backgroundColor: value ? "#0a0a0a" : "#E0E0E0", position: "relative", flexShrink: 0 }}>
        <div style={{ position: "absolute", top: 2, left: value ? 22 : 2, width: 20, height: 20, borderRadius: "50%", backgroundColor: "white", transition: "left 0.15s" }} />
      </div>
    </div>
  );
}

// Tranche d'âge optionnelle — deux compteurs côte à côte, 0/99 = "Tout âge", jamais bloquant.
// Port exact du mobile (app/(tabs)/create.tsx, AgeRangeField).
const AGE_VALUES = Array.from({ length: 82 }, (_, i) => i + 18); // 18..99 — OLTODO est réservé aux 18 ans et plus

// Opt-in explicite ("il faut d'abord vouloir une limite") : masqué par défaut derrière une case
// à cocher plutôt qu'affiché d'emblée. Deux <select> plutôt que les compteurs -/+ d'origine,
// jugés "pas bons" — sur mobile (Safari/Chrome), un <select> s'ouvre déjà en molette native,
// l'équivalent web direct de la molette d'heure de Connection (WhenStep.tsx, <input type="time">).
function AgeRangeField({ hasLimit, onToggleLimit, minAge, maxAge, onChangeMin, onChangeMax }: {
  hasLimit: boolean; onToggleLimit: (v: boolean) => void;
  minAge: number; maxAge: number; onChangeMin: (v: number) => void; onChangeMax: (v: number) => void;
}) {
  const { lang } = useApp();
  const selectStyle: React.CSSProperties = { width: "100%", padding: "11px 14px", borderRadius: 10, border: "1px solid #E0E0E0", backgroundColor: "#F8F8F8", fontSize: 14, outline: "none" };
  return (
    <div style={{ marginBottom: 16 }}>
      <div onClick={() => onToggleLimit(!hasLimit)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 12, border: "1px solid #E0E0E0", cursor: "pointer", marginBottom: hasLimit ? 10 : 0 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{t(lang, "cr_limit_age")}</div>
          <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>{t(lang, "cr_limit_age_sub")}</div>
        </div>
        <div style={{ width: 44, height: 24, borderRadius: 12, backgroundColor: hasLimit ? "#0a0a0a" : "#E0E0E0", position: "relative", flexShrink: 0 }}>
          <div style={{ position: "absolute", top: 2, left: hasLimit ? 22 : 2, width: 20, height: 20, borderRadius: "50%", backgroundColor: "white", transition: "left 0.15s" }} />
        </div>
      </div>
      {hasLimit && (
        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#999", marginBottom: 6 }}>{t(lang, "cr_age_min")}</div>
            <select value={minAge} onChange={e => onChangeMin(Math.min(Number(e.target.value), maxAge))} style={selectStyle}>
              {AGE_VALUES.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#999", marginBottom: 6 }}>{t(lang, "cr_age_max")}</div>
            <select value={maxAge} onChange={e => onChangeMax(Math.max(Number(e.target.value), minAge))} style={selectStyle}>
              {AGE_VALUES.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

// Un feedback doit toujours être rattaché à une expérience réellement vécue — jamais une
// saisie libre. On ne propose donc que les sorties/services passés, rituels et communautés
// auxquels l'utilisateur a vraiment pris part.
function FeedbackSourcePicker({ value, onSelect }: { value: { type: string; id: string } | null; onSelect: (type: string, id: string, label: string, category?: string) => void }) {
  const { lang } = useApp();
  const [sources, setSources] = React.useState<{ passions: any[]; powers: any[]; rituals: any[]; communities: any[] } | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    api.get("/publications/feedback-sources")
      .then(data => setSources(data))
      .catch(() => setSources({ passions: [], powers: [], rituals: [], communities: [] }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ fontSize: 13, color: "#999", padding: "12px 0" }}>{t(lang, "cr_loading_activities")}</div>;

  const groups = [
    { key: "passion", label: t(lang, "cr_fb_src_passion"), items: sources?.passions || [], nameKey: "title" },
    { key: "power", label: t(lang, "cr_fb_src_power"), items: sources?.powers || [], nameKey: "title" },
    { key: "ritual", label: t(lang, "side_menu_my_rituals"), items: sources?.rituals || [], nameKey: "title" },
    { key: "community", label: t(lang, "side_menu_my_communities"), items: sources?.communities || [], nameKey: "name" },
  ].filter(g => g.items.length > 0);

  if (groups.length === 0) {
    return (
      <div style={{ padding: 16, backgroundColor: "#F8F8F8", borderRadius: 12, textAlign: "center" }}>
        <div style={{ fontSize: 13, color: "#666", lineHeight: 1.6 }}>
          {t(lang, "cr_fb_no_activity")}
        </div>
      </div>
    );
  }

  return (
    <div>
      {groups.map(g => (
        <div key={g.key} style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#999", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>{g.label}</div>
          {g.items.map((item: any) => {
            const selected = value?.type === g.key && value?.id === item.id;
            const label = item[g.nameKey];
            return (
              <div key={item.id} onClick={() => onSelect(g.key, item.id, label, item.category)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 12px", borderRadius: 10, border: `1px solid ${selected ? "#0a0a0a" : "#E0E0E0"}`, backgroundColor: selected ? "#F0F0F0" : "white", marginBottom: 6, cursor: "pointer" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
                  {item.event_date && <div style={{ fontSize: 11, color: "#999" }}>{new Date(item.event_date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</div>}
                </div>
                {selected && <span style={{ color: "#0a0a0a", fontWeight: 700 }}>✓</span>}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// Les personnes qu'on peut mentionner sont toujours les vraies personnes de l'activité
// liée (organisateur + participants confirmés, ou membres du rituel/de la communauté) —
// jamais une liste d'amis générique, pour rester fidèle à ce qui s'est vraiment passé.
function MentionPicker({ refType, refId, selected, onToggle }: { refType: string; refId: string; selected: string[]; onToggle: (id: string) => void }) {
  const { lang } = useApp();
  const [people, setPeople] = React.useState<{ id: string; name: string; avatar_url?: string }[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    setLoading(true);
    api.get(`/publications/feedback-people?ref_type=${refType}&ref_id=${refId}`)
      .then(data => setPeople(data?.people || []))
      .catch(() => setPeople([]))
      .finally(() => setLoading(false));
  }, [refType, refId]);

  if (loading) return <div style={{ fontSize: 13, color: "#999", padding: "8px 0" }}>{t(lang, "loading_ellipsis")}</div>;
  if (people.length === 0) return null;

  return (
    <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 8, marginBottom: 4 }}>
      {people.map(p => {
        const isSelected = selected.includes(p.id);
        return (
          <div key={p.id} onClick={() => onToggle(p.id)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px 6px 6px", borderRadius: 20, border: `1px solid ${isSelected ? "#0a0a0a" : "#E0E0E0"}`, backgroundColor: isSelected ? "#0a0a0a" : "white", cursor: "pointer" }}>
            <div style={{ width: 22, height: 22, borderRadius: "50%", backgroundColor: isSelected ? "white" : "#0a0a0a", color: isSelected ? "#0a0a0a" : "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, overflow: "hidden", flexShrink: 0 }}>
              {p.avatar_url ? <img src={p.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : p.name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
            </div>
            <span style={{ fontSize: 13, fontWeight: 600, color: isSelected ? "white" : "#0a0a0a" }}>{p.name}</span>
          </div>
        );
      })}
    </div>
  );
}

// Variante de FriendSelector pour un Pro Event : on invite parmi les membres et abonnés du LIEU
// qui publie, jamais les amis personnels de l'agent connecté (qui n'ont aucun rapport avec le
// lieu — voir OLTODO-backend/src/utils/invites.ts#inviteVenuePeople, le pendant serveur de ce
// composant, sans lequel les invitations envoyées ici seraient silencieusement ignorées).
//
// Membres et abonnés sont deux populations très différentes (des dizaines pour l'un,
// potentiellement des milliers pour l'autre) : jamais mélangées dans une seule liste, chacune
// avec sa propre recherche et son propre "Tout sélectionner" (qui récupère TOUS les id en une
// requête légère — GET .../members|followers?ids_only=1 — sans jamais avoir à charger/afficher
// des milliers d'avatars). L'organisateur est toujours exclu de la liste : s'auto-inviter à son
// propre événement n'a pas de sens. Liste verticale plutôt que le défilement horizontal
// d'origine, qui rendait la page instable au doigt sur mobile web (glissement latéral parasite).
function PersonSection({ title, venueId, endpoint, selected, onToggle, onToggleMany, excludeId }: {
  title: string; venueId: string; endpoint: "members" | "followers";
  selected: string[]; onToggle: (id: string) => void; onToggleMany: (ids: string[], add: boolean) => void; excludeId?: string;
}) {
  const { lang } = useApp();
  const [people, setPeople] = useState<any[] | null>(null);
  const [query, setQuery] = useState("");
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [selectingAll, setSelectingAll] = useState(false);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = (q: string) => {
    const qs = q ? `?q=${encodeURIComponent(q)}` : "";
    api.get(`/venues/${venueId}/${endpoint}${qs}`).then(data => {
      const rows: any[] = endpoint === "members" ? (data?.members || []) : (data?.followers || []);
      const mapped = rows
        .map(r => ({ id: endpoint === "members" ? r.user_id : r.id, name: r.name, avatar_url: r.avatar_url }))
        .filter(p => p.id !== excludeId);
      setPeople(mapped);
      if (!q) setTotalCount(mapped.length);
    }).catch(() => setPeople([]));
  };

  React.useEffect(() => { load(""); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [venueId]);

  const onQueryChange = (v: string) => {
    setQuery(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(v.trim()), 300);
  };

  const selectedInSection = people ? people.filter(p => selected.includes(p.id)).length : 0;
  const allVisibleSelected = !!people && people.length > 0 && selectedInSection === people.length;

  const toggleAll = async () => {
    if (allVisibleSelected) { onToggleMany((people || []).map(p => p.id), false); return; }
    setSelectingAll(true);
    try {
      const data = await api.get(`/venues/${venueId}/${endpoint}?ids_only=1`);
      const ids: string[] = (data?.ids || []).filter((id: string) => id !== excludeId);
      onToggleMany(ids, true);
    } catch {
      onToggleMany((people || []).map(p => p.id), true);
    }
    setSelectingAll(false);
  };

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 700 }}>{title}{totalCount != null ? ` (${totalCount})` : ""}</span>
        {!!people?.length && (
          <button onClick={toggleAll} disabled={selectingAll} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12.5, fontWeight: 700, color: "#0a0a0a", padding: 0 }}>
            {selectingAll ? "..." : allVisibleSelected ? t(lang, "invite_contacts_deselect_all") : t(lang, "invite_contacts_select_all")}
          </button>
        )}
      </div>
      <input
        value={query} onChange={e => onQueryChange(e.target.value)}
        placeholder={t(lang, "cr_search_among").replace("{x}", title.toLowerCase())}
        style={{ width: "100%", padding: "9px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 13.5, outline: "none", backgroundColor: "#F8F8F8", boxSizing: "border-box", marginBottom: 4 }}
      />
      {people === null ? (
        <div style={{ fontSize: 13, color: "#999", padding: "8px 0" }}>{t(lang, "loading_ellipsis")}</div>
      ) : people.length === 0 ? (
        <div style={{ fontSize: 12.5, color: "#999", marginTop: 8 }}>
          {query ? t(lang, "invite_contacts_empty") : endpoint === "members" ? t(lang, "cr_no_members_yet") : t(lang, "cr_no_followers_yet")}
        </div>
      ) : (
        <div style={{ marginTop: 4 }}>
          {people.map(person => {
            const active = selected.includes(person.id);
            return (
              <div key={person.id} onClick={() => onToggle(person.id)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", cursor: "pointer" }}>
                <div style={{ width: 34, height: 34, borderRadius: "50%", overflow: "hidden", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 12, flexShrink: 0 }}>
                  {person.avatar_url ? <img src={person.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : (person.name || "?").split(" ").map((n: string) => n[0]).join("").slice(0, 2).toUpperCase()}
                </div>
                <span style={{ flex: 1, fontSize: 13.5, fontWeight: active ? 700 : 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{person.name}</span>
                <div style={{ width: 20, height: 20, borderRadius: 5, border: `1.5px solid ${active ? "#0a0a0a" : "#E0E0E0"}`, backgroundColor: active ? "#0a0a0a" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {active && <span style={{ color: "white", fontSize: 12 }}>✓</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function VenuePeopleSelector({ venueId, selected, onToggle, onToggleMany }: {
  venueId: string; selected: string[]; onToggle: (id: string) => void; onToggleMany: (ids: string[], add: boolean) => void;
}) {
  const { user, lang } = useApp();
  if (!venueId) return null;
  return (
    <div>
      <PersonSection title={t(lang, "community_members")} venueId={venueId} endpoint="members" selected={selected} onToggle={onToggle} onToggleMany={onToggleMany} excludeId={user?.id} />
      <PersonSection title={t(lang, "cr_followers")} venueId={venueId} endpoint="followers" selected={selected} onToggle={onToggle} onToggleMany={onToggleMany} excludeId={user?.id} />
    </div>
  );
}

type PartnerVenue = { id: string; name: string; type: string; address: string };

// Sélection multiple de lieux Pro/Association à inviter comme co-organisateurs d'un Pro Event
// (voir OLTODO-backend/src/routes/publications.ts#POST /:id/collaborators). Chaque lieu invité
// doit accepter avant que l'événement apparaisse sur SA propre page — on ne fait ici que
// constituer la liste envoyée à la création, jamais d'invitation immédiate.
function PartnerVenuesField({ excludeVenueId, selected, onChange }: {
  excludeVenueId: string; selected: PartnerVenue[]; onChange: (venues: PartnerVenue[]) => void;
}) {
  const { lang } = useApp();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<PartnerVenue[]>([]);
  const [searching, setSearching] = useState(false);

  React.useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    const timer = setTimeout(() => {
      api.get(`/venues/search?q=${encodeURIComponent(query)}&subtype=pro,association&exclude_id=${excludeVenueId}`)
        .then(data => setResults(data?.venues || []))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 400);
    return () => clearTimeout(timer);
  }, [query, excludeVenueId]);

  const add = (v: PartnerVenue) => {
    if (selected.some(s => s.id === v.id)) return;
    onChange([...selected, v]);
    setQuery(""); setResults([]);
  };
  const remove = (id: string) => onChange(selected.filter(s => s.id !== id));

  return (
    <div>
      {selected.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
          {selected.map(v => (
            <div key={v.id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", borderRadius: 16, border: "1px solid #E0E0E0", backgroundColor: "#F8F8F8" }}>
              <span style={{ fontSize: 12.5, fontWeight: 600 }}>{v.name}</span>
              <span onClick={() => remove(v.id)} style={{ cursor: "pointer", color: "#999", fontSize: 13, lineHeight: 1 }}>✕</span>
            </div>
          ))}
        </div>
      )}
      <input
        value={query} onChange={e => setQuery(e.target.value)}
        placeholder={t(lang, "cr_search_partner_venue")}
        style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: "1px solid #E0E0E0", backgroundColor: "#F8F8F8", fontSize: 14, outline: "none", boxSizing: "border-box" }}
      />
      {searching && <div style={{ fontSize: 12, color: "#999", marginTop: 6 }}>{t(lang, "cr_searching")}</div>}
      {!searching && results.length > 0 && (
        <div style={{ border: "1px solid #E0E0E0", borderRadius: 12, marginTop: 6, overflow: "hidden" }}>
          {results.map(v => (
            <div key={v.id} onClick={() => add(v)} style={{ padding: 12, borderBottom: "1px solid #F5F5F5", cursor: "pointer" }}>
              <div style={{ fontSize: 13.5, fontWeight: 600 }}>{v.name}</div>
              <div style={{ fontSize: 11, color: "#999" }}>{v.type} · {v.address}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────────────────────
export default function Create() {
  const { lang, token, user, setSideMenuOpen } = useApp();
  const navigate = useNavigate();
  const audienceOpts = AUDIENCE_KEYS.map(a => ({ key: a.key, label: t(lang, a.labelKey), sub: t(lang, a.subKey) }));
  const feedbackAudienceOpts = audienceOpts.filter(a => a.key !== "prive");
  const [searchParams] = useSearchParams();
  // Arrivée depuis la fiche d'un lieu/communauté/rituel administré ("+ Ajouter une
  // publication") : l'entité est déjà connue, on saute directement à l'étape de contenu au lieu
  // de refaire chercher l'adresse, et son identité est présélectionnée dans le sélecteur
  // "Publier en tant que" (voir useEffect ci-dessous) — jamais imposée, juste par défaut.
  const prefillVenueId = searchParams.get("venue_id");
  const prefillVenueName = searchParams.get("venue_name");
  const prefillCommunityId = searchParams.get("community_id");
  const prefillRitualId = searchParams.get("ritual_id");
  const prefillEntityId = prefillVenueId || prefillCommunityId || prefillRitualId;
  // Arrivée depuis "+ Ajouter un Pro Event" (bouton propre, voir Venue.tsx onglet Pro Events) —
  // même saut direct à l'étape de contenu que venue_id, mais domaine forcé à pro_event.
  const prefillProEvent = searchParams.get("pro_event");
  // Arrivée depuis "+ Proposer un trajet" (voir pages/Move.tsx) — même saut direct au domaine
  // MOVE que pro_event ci-dessus, jamais un "?domain=" générique inventé.
  const prefillMove = searchParams.get("move");
  // Arrivée depuis un rappel d'anniversaire (J-30/J-7, voir Notifications.tsx) — même saut direct
  // sur une Passion, catégorie et ami déjà pré-remplis pour inciter à organiser tout de suite.
  const prefillBirthdayFriendId = searchParams.get("birthday_friend_id");
  const prefillBirthdayFriendName = searchParams.get("birthday_friend_name") || "";
  const [uploading, setUploading] = useState(false);
  const [showLive, setShowLive] = useState(false);

  const PERSONAL_IDENTITY: PublishIdentity = { type: "user", id: null, name: "Personnel", avatar_url: user?.avatar_url || null, verified: false, role: "self" };
  const [identities, setIdentities] = useState<PublishIdentity[]>([PERSONAL_IDENTITY]);
  const [selectedIdentity, setSelectedIdentity] = useState<PublishIdentity>(PERSONAL_IDENTITY);
  const [showPublishAs, setShowPublishAs] = useState(false);

  React.useEffect(() => {
    api.get("/users/me/publishing-identities").then(data => {
      const list: PublishIdentity[] = data?.identities?.length ? data.identities : [PERSONAL_IDENTITY];
      setIdentities(list);
      const ctxType = prefillVenueId ? "venue" : prefillCommunityId ? "community" : prefillRitualId ? "ritual" : null;
      if (ctxType && prefillEntityId) {
        const match = list.find(i => i.type === ctxType && i.id === prefillEntityId);
        if (match) setSelectedIdentity(match);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recadrage avant envoi — port du même mécanisme que Profile.tsx (voir ImageCropModal.tsx) :
  // jusqu'ici la couverture d'une publication partait à l'upload telle quelle, sans aucune étape
  // de cadrage/zoom (contrairement à la photo de profil, déjà corrigée), puis se retrouvait
  // recadrée "à l'aveugle" par le center/cover de PostCard.tsx — d'où la sensation de photo mal
  // cadrée/étirée : jamais un vrai étirement CSS, mais aucun moyen de choisir ce qui reste visible.
  const [coverCropFile, setCoverCropFile] = useState<File | null>(null);
  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // permet de rechoisir le même fichier après une annulation du crop
    if (file) setCoverCropFile(file);
  };
  const confirmCoverCrop = async (blob: Blob) => {
    setCoverCropFile(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", blob, "cover.webp");
      const res = await authFetch("/publications/upload", { method: "POST", body: fd });
      const data = await res.json();
      if (data.url) {
        setPhotoUrl(data.url);
        setHasPhoto(true);
      } else {
        alert(t(lang, "cr_err_upload_photo"));
      }
    } catch {
      alert(t(lang, "cr_err_upload_conn"));
    } finally {
      setUploading(false);
    }
  };
  const [step, setStep] = useState((prefillVenueId || prefillBirthdayFriendId) ? 2 : 1);
  const [domain, setDomain] = useState(prefillProEvent ? "pro_event" : prefillMove ? "move" : (prefillVenueId || prefillBirthdayFriendId) ? "passion" : "");
  const [category, setCategory] = useState(prefillBirthdayFriendId ? "Soirée d'anniversaire" : "");
  const [title, setTitle] = useState(prefillBirthdayFriendId ? t(lang, "cr_birthday_title_default").replace("{name}", prefillBirthdayFriendName).trim() : "");
  const [description, setDescription] = useState("");
  const [hasPhoto, setHasPhoto] = useState(false);
  const [photoFile, setPhotoFile] = useState<File|null>(null);
  const [photoUrl, setPhotoUrl] = useState<string>("");
  // Un feedback peut porter plusieurs photos/vidéos (façon Facebook/LinkedIn), contrairement à
  // une passion/power qui n'a qu'une seule couverture — état séparé exprès.
  const [feedbackMedia, setFeedbackMedia] = useState<{ url: string; media_type: "image" | "video" }[]>([]);
  const [uploadingFeedbackMedia, setUploadingFeedbackMedia] = useState(false);
  const MAX_FEEDBACK_MEDIA = 6;

  const handleFeedbackMediaSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    const room = MAX_FEEDBACK_MEDIA - feedbackMedia.length;
    if (room <= 0) return;
    setUploadingFeedbackMedia(true);
    try {
      for (const file of files.slice(0, room)) {
        const isVideo = file.type.startsWith("video/");
        const toSend = isVideo ? file : await compressImage(file);
        const fd = new FormData();
        fd.append("file", toSend);
        const res = await authFetch("/uploads/publication", { method: "POST", body: fd });
        const data = await res.json();
        if (data?.url) {
          setFeedbackMedia(prev => [...prev, { url: data.url, media_type: isVideo ? "video" : "image" }]);
        }
      }
    } catch {
      alert(t(lang, "cr_err_upload_media"));
    } finally {
      setUploadingFeedbackMedia(false);
    }
  };
  const removeFeedbackMedia = (url: string) => setFeedbackMedia(prev => prev.filter(m => m.url !== url));
  const [feedbackRefType, setFeedbackRefType] = useState<string>("");
  const [feedbackRefId, setFeedbackRefId] = useState<string>("");
  const [mentionedUserIds, setMentionedUserIds] = useState<string[]>([]);
  const [locationText, setLocationText] = useState(prefillVenueName || "");
  const [venueId, setVenueId] = useState<string | null>(prefillVenueId || null);
  const [locationLat, setLocationLat] = useState<number | null>(null);
  const [locationLng, setLocationLng] = useState<number | null>(null);
  // MOVE — mode + véhicule (Ride/Scoot uniquement) + destination (un 2e point géographique, la
  // seule publication à en avoir deux — locationText/Lat/Lng ci-dessus servent de DÉPART, comme
  // pour un Passion). destinationVenueId n'est jamais envoyé au serveur : la destination reste un
  // simple point géographique, jamais liée à une fiche lieu (voir le plan MOVE).
  const [moveMode, setMoveMode] = useState<MoveMode | null>(null);
  const [vehicleMake, setVehicleMake] = useState("");
  const [vehicleModel, setVehicleModel] = useState("");
  const [destinationText, setDestinationText] = useState("");
  const [destinationLat, setDestinationLat] = useState<number | null>(null);
  const [destinationLng, setDestinationLng] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedHour, setSelectedHour] = useState(19);
  const [selectedMinute, setSelectedMinute] = useState("00");
  // Choisir aujourd'hui ne doit pas permettre une heure déjà passée (ex: 09h alors qu'il est
  // 20h) — le <input type="date"> bloque déjà les jours passés via min={todayISO}, mais rien ne
  // filtrait l'heure/minute pour le jour même. validHoursFor/validMinutesFor masquent les
  // créneaux déjà passés ; handleDateChange rattrape la sélection si elle devient invalide en
  // choisissant "aujourd'hui" après coup.
  const validHoursFor = (d: string) => {
    if (d !== todayISO) return Array.from({ length: 24 }, (_, i) => i);
    const now = new Date();
    return Array.from({ length: 24 }, (_, i) => i).filter(h => h > now.getHours() || (h === now.getHours() && ["00","15","30","45"].some(m => Number(m) > now.getMinutes())));
  };
  const validMinutesFor = (d: string, h: number) => {
    if (d !== todayISO) return ["00","15","30","45"];
    const now = new Date();
    if (h !== now.getHours()) return ["00","15","30","45"];
    return ["00","15","30","45"].filter(m => Number(m) > now.getMinutes());
  };
  const handleDateChange = (v: string) => {
    setSelectedDate(v);
    if (v !== todayISO) return;
    const hours = validHoursFor(v);
    if (!hours.includes(selectedHour)) {
      const nextHour = hours[0] ?? new Date().getHours();
      setSelectedHour(nextHour);
      const mins = validMinutesFor(v, nextHour);
      if (mins.length) setSelectedMinute(mins[0]);
    } else {
      const mins = validMinutesFor(v, selectedHour);
      if (mins.length && !mins.includes(selectedMinute)) setSelectedMinute(mins[0]);
    }
  };
  const [price, setPrice] = useState("");
  const [isFree, setIsFree] = useState(false);
  const [duration, setDuration] = useState<number>(2);
  const [places, setPlaces] = useState("");
  // Opt-in explicite, même recette que "Gratuit" (price) et "Limiter l'âge" — "Illimité" est actif
  // par défaut (places === "") ; un lien de raccourci toujours visible sous le compteur laissait
  // penser à tort que la limite ne s'appliquait jamais, même après être passé à 2 ou 3.
  const [hasPlacesLimit, setHasPlacesLimit] = useState(false);
  const toggleHasPlacesLimit = (v: boolean) => { setHasPlacesLimit(v); setPlaces(v ? "2" : ""); };
  const [locationType, setLocationType] = useState<"home" | "provider" | "remote">("home");
  const [audience, setAudience] = useState("public");
  const [needApproval, setNeedApproval] = useState(false);
  const [waitlistEnabled, setWaitlistEnabled] = useState(true);
  const [selectedFriends, setSelectedFriends] = useState<string[]>(prefillBirthdayFriendId ? [prefillBirthdayFriendId] : []);
  // Lieux Pro/Association invités à co-organiser un Pro Event (voir PartnerVenuesField) —
  // distinct de selectedFriends, qui ne sert qu'à inviter des PERSONNES. Objets complets (pas
  // juste des id) pour pouvoir afficher leur nom en chip sans requête supplémentaire.
  const [partnerVenues, setPartnerVenues] = useState<{ id: string; name: string; type: string; address: string }[]>([]);
  const [frequence, setFrequence] = useState("");
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [communityName, setCommunityName] = useState("");
  const [minScore, setMinScore] = useState(0);
  // Tranche d'âge — optionnelle, mêmes bornes par défaut que le backend (18/99 = "Tout âge") pour
  // Passion, Communauté et Rituel. Absente pour Power/Feedback (jamais demandée pour ceux-là).
  // 18 et non 0 : OLTODO est réservé aux 18 ans et plus, aucun membre ne peut être plus jeune.
  const [hasAgeLimit, setHasAgeLimit] = useState(false);
  const [minAge, setMinAge] = useState(18);
  const [maxAge, setMaxAge] = useState(99);
  const toggleAgeLimit = (v: boolean) => { setHasAgeLimit(v); if (!v) { setMinAge(18); setMaxAge(99); } };

  // Ritual et Community restent sur la taxonomie statique (hors périmètre du catalogue DB) —
  // passion/power sont désormais rendus par CatalogCategoryDropdown, qui parle directement au
  // catalogue partagé, donc plus besoin de leur fournir cats/groups ici.
  const getCats = () => {
    if (domain === "ritual") return TOUS_RITUAL;
    if (domain === "community") return TOUS_COMMUNITY;
    if (domain === "pro_event") return TOUS_PRO_EVENTS;
    return [];
  };
  const getGroups = (): CategoryGroup[] | undefined => {
    if (domain === "ritual") return RITUAL_GROUPS;
    if (domain === "pro_event") return PRO_EVENT_GROUPS;
    return undefined;
  };

  const totalSteps = domain === "feedback" ? 2 : domain === "ritual" ? 3 : domain === "community" ? 2 : domain === "move" ? 3 : 4;

  const handleVenueChange = (v: string, id?: string | null, lat?: number | null, lng?: number | null) => {
    setLocationText(v);
    setVenueId(id || null);
    setLocationLat(lat ?? null);
    setLocationLng(lng ?? null);
  };

  const handleSubmit = () => {
    if (domain === "community") {
      api.post("/communities", {
        name: communityName,
        category,
        description,
        adhesion: audience === "public" ? "public" : "invitation",
        invited_user_ids: selectedFriends,
        cover_url: photoUrl || null,
        min_age: minAge, max_age: maxAge,
      }).then(data => {
        if (data?.community) navigate(`/community/${data.community.id}`);
        else alert(t(lang, "cr_err_create_community"));
      }).catch(() => alert(t(lang, "err_connection_short")));
      return;
    }

    if (domain === "ritual") {
      const startDate = selectedDate ? selectedDate.split("/").reverse().join("-") : null;
      api.post("/rituals", {
        title,
        category,
        description,
        frequency: frequence,
        days: selectedDays,
        time_of_day: `${String(selectedHour).padStart(2, "0")}:${selectedMinute}:00`,
        location_name: locationText,
        latitude: locationLat,
        longitude: locationLng,
        start_date: startDate,
        audience,
        invited_user_ids: selectedFriends,
        cover_url: photoUrl || null,
        min_age: minAge, max_age: maxAge,
      }).then(data => {
        if (data?.ritual) navigate("/profile?tab=Rituels");
        else alert(t(lang, "cr_err_create_ritual"));
      }).catch(() => alert(t(lang, "err_connection_short")));
      return;
    }

    const body = {
      type: domain,
      category,
      title,
      description,
      price: isFree ? 0 : Number(price),
      is_free: isFree,
      max_places: places ? Number(places) : null,
      audience,
      location_name: locationText,
      latitude: locationLat,
      longitude: locationLng,
      // Un Pro Event ne se tague jamais sur la colonne venue_id générique (réservée aux
      // événements publics affichés sur la fiche lieu) — son rattachement au lieu passe
      // uniquement par posted_as_type/posted_as_id, déjà vérifié serveur (POST /publications).
      venue_id: domain === "pro_event" ? null : venueId,
      event_date: selectedDate ? `${selectedDate.split("/").reverse().join("-")}T${String(selectedHour).padStart(2,"00")}:${selectedMinute}:00` : null,
      min_score: minScore,
      needs_approval: needApproval,
      waitlist_enabled: waitlistEnabled,
      min_age: domain === "passion" ? minAge : undefined,
      max_age: domain === "passion" ? maxAge : undefined,
      cover_url: domain === "feedback" ? (feedbackMedia[0]?.url || null) : (photoUrl || null),
      media: domain === "feedback" ? feedbackMedia : undefined,
      feedback_ref_type: domain === "feedback" ? feedbackRefType : undefined,
      feedback_ref_id: domain === "feedback" ? feedbackRefId : undefined,
      mentioned_user_ids: domain === "feedback" ? mentionedUserIds : undefined,
      invited_user_ids: domain !== "feedback" ? selectedFriends : undefined,
      location_type: domain === "power" ? locationType : undefined,
      move_mode: domain === "move" ? moveMode : undefined,
      destination_latitude: domain === "move" ? destinationLat : undefined,
      destination_longitude: domain === "move" ? destinationLng : undefined,
      destination_name: domain === "move" ? destinationText : undefined,
      vehicle_make: domain === "move" && moveMode === "ride" ? vehicleMake : undefined,
      vehicle_model: domain === "move" && moveMode === "ride" ? vehicleModel : undefined,
      posted_as_type: selectedIdentity.type,
      posted_as_id: selectedIdentity.id,
    };
    api.post("/publications", body).then(data => {
      if (data?.publication) {
        // Best-effort : un échec ici n'invalide jamais la création de l'événement lui-même,
        // l'organisateur peut réessayer d'inviter des partenaires depuis la fiche du lieu.
        if (domain === "pro_event" && partnerVenues.length > 0) {
          api.post(`/publications/${data.publication.id}/collaborators`, { venue_ids: partnerVenues.map(v => v.id) }).catch(() => {});
        }
        navigate("/");
      }
      else alert(data?.error || t(lang, "cr_err_publish"));
    }).catch(() => alert(t(lang, "err_connection_short")));
  };

  const canNext = () => {
    if (step === 1) return domain !== "";
    if (step === 2) {
      if (domain === "community") return communityName !== "" && description !== "" && hasPhoto;
      if (domain === "ritual") return category !== "" && title !== "" && frequence !== "" && hasPhoto;
      if (domain === "feedback") return feedbackRefId !== "" && description.trim() !== "" && feedbackMedia.length > 0;
      if (domain === "move") {
        if (!moveMode || title === "") return false;
        if (moveMode === "ride" && vehicleMake === "") return false;
        return true;
      }
      return category !== "" && title !== "" && description !== "" && hasPhoto;
    }
    if (step === 3 && domain === "move") {
      if (selectedDate !== "") {
        const chosen = new Date(`${selectedDate}T${String(selectedHour).padStart(2,"0")}:${selectedMinute}:00`);
        if (chosen.getTime() <= Date.now()) return false;
      }
      return locationText !== "" && locationLat != null && destinationText !== "" && destinationLat != null && selectedDate !== "" && Number(places || "0") >= 1;
    }
    return true;
  };

  const toggleFriend = (id: string) => setSelectedFriends(prev => prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]);
  // "Tout sélectionner"/"Tout désélectionner" d'une section entière (VenuePeopleSelector) — évite
  // un appel à toggleFriend par personne quand un lieu compte des dizaines/centaines de membres.
  const toggleFriendsMany = (ids: string[], add: boolean) => setSelectedFriends(prev => {
    if (add) return Array.from(new Set([...prev, ...ids]));
    const remove = new Set(ids);
    return prev.filter(f => !remove.has(f));
  });
  const toggleDay = (day: string) => setSelectedDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);

  const inputStyle: React.CSSProperties = { width: "100%", padding: "11px 14px", borderRadius: 10, border: "1px solid #E0E0E0", backgroundColor: "#F8F8F8", fontSize: 14, outline: "none", marginBottom: 4 };
  const labelStyle: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: "#0a0a0a", display: "block", marginBottom: 8, marginTop: 16 };
  const card: React.CSSProperties = { backgroundColor: "white", borderRadius: 16, padding: 20, marginBottom: 12, border: "1px solid #F0F0F0" };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <div style={{ flex: 1, overflowY: "auto", paddingBottom: 100 }}>
        {/* Header */}
        <div style={{ backgroundColor: "white", padding: "12px 16px 10px", position: "sticky", top: 0, zIndex: 20, borderBottom: "1px solid #F0F0F0" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            {step > 1 ? <button onClick={() => setStep(step - 1)} style={{ background: "none", border: "none", cursor: "pointer", color: "#0a0a0a", fontSize: 20, display: "flex", alignItems: "center" }}>←</button> : <div style={{ width: 24 }} />}
            <span style={{ color: "#0a0a0a", fontWeight: 700, fontSize: 15 }}>{t(lang, "create_publication_entry")}</span>
            <button onClick={() => navigate("/")} style={{ background: "none", border: "none", cursor: "pointer", color: "#999", fontSize: 20 }}>✕</button>
          </div>
          {/* Progress */}
          <div style={{ display: "flex", gap: 4, marginBottom: 5 }}>
            {Array.from({ length: totalSteps }, (_, i) => (
              <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, backgroundColor: i < step ? "#0a0a0a" : "#E0E0E0" }} />
            ))}
          </div>
          <span style={{ fontSize: 11, color: "#999" }}>{t(lang, "cr_step_indicator").replace("{n}", String(step)).replace("{total}", String(totalSteps))}</span>
        </div>

        {/* "Publier en tant que" — discret, visible à toutes les étapes (pas seulement la
            première), changeable en un tap. Masqué pour les domaines community/ritual : ce sont
            des flux de création d'entité, pas des publications au nom d'une identité. */}
        {domain !== "community" && domain !== "ritual" && (
          <div style={{ padding: "10px 20px 0", maxWidth: 700, margin: "0 auto", boxSizing: "border-box" }}>
            <button
              onClick={() => setShowPublishAs(true)}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 0", background: "none", border: "none", cursor: "pointer", color: "#666", fontSize: 13, fontWeight: 600 }}
            >
              {t(lang, "publish_as_prefix")} {selectedIdentity.type === "user" ? t(lang, "publish_as_personal") : selectedIdentity.name}
              <ChevronDown size={14} />
            </button>
          </div>
        )}

        {showPublishAs && (
          <PublishAsSheet
            lang={lang}
            identities={identities}
            selected={selectedIdentity}
            onSelect={setSelectedIdentity}
            onClose={() => setShowPublishAs(false)}
            onManageIdentities={() => setSideMenuOpen(true)}
          />
        )}

        <div style={{ padding: 20, maxWidth: 700, margin: "0 auto" }}>

          {/* ÉTAPE 1 — Domaine */}
          {step === 1 && (
            <div>
              <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{t(lang, "cr_q_title")}</h2>
              <p style={{ fontSize: 13, color: "#999", marginBottom: 20 }}>{t(lang, "cr_q_sub")}</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {DOMAINS.map(d => (
                  <div key={d.key} onClick={() => { setDomain(d.key); setCategory(""); if (d.key === "feedback" && audience === "prive") setAudience("public"); if (d.key === "move") { setHasPlacesLimit(true); setPlaces(p => p || "1"); } }} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 18px", borderRadius: 14, border: `1px solid ${domain === d.key ? "#0a0a0a" : "#E0E0E0"}`, backgroundColor: domain === d.key ? "#0a0a0a" : "white", cursor: "pointer" }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 800, letterSpacing: 1, color: domain === d.key ? "white" : "#0a0a0a", marginBottom: 4 }}>{d.label}</div>
                      <div style={{ fontSize: 12, color: domain === d.key ? "rgba(255,255,255,0.5)" : "#999" }}>{t(lang, d.descKey)}</div>
                    </div>
                    {domain === d.key && <div style={{ width: 24, height: 24, borderRadius: "50%", backgroundColor: "white", display: "flex", alignItems: "center", justifyContent: "center", color: "#0a0a0a", fontWeight: 700, fontSize: 14 }}>✓</div>}
                  </div>
                ))}
                {/* Pro Events : jamais pour un compte personnel, uniquement en publiant au nom
                    d'un lieu Pro/Association (venueSubtype 'pro'/'association', exposé par
                    GET /users/me/publishing-identities). Juste avant LIVE, comme demandé. */}
                {selectedIdentity.type === "venue" && (selectedIdentity.venueSubtype === "pro" || selectedIdentity.venueSubtype === "association") && (
                  <div onClick={() => { setDomain("pro_event"); setCategory(""); }} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 18px", borderRadius: 14, border: `1.5px solid ${domain === "pro_event" ? "#7A9E28" : "#9ACD32"}`, backgroundColor: domain === "pro_event" ? "#9ACD32" : "white", cursor: "pointer" }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 800, letterSpacing: 1, color: domain === "pro_event" ? "#0a0a0a" : "#7A9E28", marginBottom: 4 }}>PRO EVENTS</div>
                      <div style={{ fontSize: 12, color: domain === "pro_event" ? "#0a0a0a" : "#7A9E28", opacity: domain === "pro_event" ? 0.7 : 0.7 }}>{t(lang, "cr_dom_pro_events_desc")}</div>
                    </div>
                    {domain === "pro_event" && <div style={{ width: 24, height: 24, borderRadius: "50%", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "#9ACD32", fontWeight: 700, fontSize: 14 }}>✓</div>}
                  </div>
                )}
                {/* Distinct des autres domaines (pas une "publication" à proprement parler) —
                    noir + bleu (retour arrière sur le vert-jaune essayé, jugé moins moderne) : le
                    texte "LIVE" et le point restent blancs, seule la bordure porte le bleu. */}
                <div onClick={() => setShowLive(true)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 18px", borderRadius: 14, border: "1.5px solid #0C32FF", backgroundColor: "#0a0a0a", cursor: "pointer" }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 800, letterSpacing: 1, color: "#FFFFFF", marginBottom: 4 }}>LIVE</div>
                    <div style={{ fontSize: 12, color: "#FFFFFF", opacity: 0.6 }}>{t(lang, "cr_dom_live_desc")}</div>
                  </div>
                  <div style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: "#FFFFFF" }} />
                </div>
              </div>
              <div onClick={() => navigate("/power-requests")} style={{ textAlign: "center", marginTop: 18, fontSize: 13, color: "#666", cursor: "pointer" }}>
                {t(lang, "cr_looking_provider")} <span style={{ color: "#0a0a0a", fontWeight: 700, textDecoration: "underline" }}>{t(lang, "cr_post_request")}</span>
              </div>
              <div onClick={() => navigate("/move")} style={{ textAlign: "center", marginTop: 10, fontSize: 13, color: "#666", cursor: "pointer" }}>
                {t(lang, "cr_want_carpool")} <span style={{ color: "#0a0a0a", fontWeight: 700, textDecoration: "underline" }}>{t(lang, "cr_see_moves")}</span>
              </div>
              <div onClick={() => navigate("/birthday-events")} style={{ textAlign: "center", marginTop: 10, fontSize: 13, color: "#666", cursor: "pointer" }}>
                {t(lang, "bday_entry_prompt")} <span style={{ color: "#0a0a0a", fontWeight: 700, textDecoration: "underline" }}>{t(lang, "bday_entry_link")}</span>
              </div>
            </div>
          )}

          {/* ÉTAPE 2 — Contenu */}
          {step === 2 && (domain === "passion" || domain === "power") && (
            <div>
              <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{domain === "passion" ? t(lang, "cr_your_passion") : t(lang, "cr_your_power")}</h2>
              <p style={{ fontSize: 13, color: "#999", marginBottom: 20 }}>{t(lang, "cr_passion_sub")}</p>
              <label style={labelStyle}>{t(lang, "cr_category")}</label>
              <CatalogCategoryDropdown catalogType={domain as "passion" | "power"} selected={category} onSelect={setCategory} placeholder={domain === "passion" ? t(lang, "cr_choose_activity") : t(lang, "cr_choose_service")} lang={lang} />
              <label style={labelStyle}>{t(lang, "cr_title")}</label>
              <input style={inputStyle} value={title} onChange={e => setTitle(e.target.value)} placeholder={domain === "passion" ? t(lang, "cr_ph_title_passion") : t(lang, "cr_ph_title_power")} maxLength={60} />
              <div style={{ fontSize: 11, color: "#999", textAlign: "right", marginBottom: 4 }}>{title.length}/60</div>
              <label style={labelStyle}>{t(lang, "cr_description")}</label>
              <textarea style={{ ...inputStyle, minHeight: 100, resize: "none" } as any} value={description} onChange={e => setDescription(e.target.value)} placeholder={t(lang, "cr_ph_desc_passion")} maxLength={300} />
              <div style={{ fontSize: 11, color: "#999", textAlign: "right", marginBottom: 4 }}>{description.length}/300</div>
              <label style={labelStyle}>{t(lang, "cr_cover_photo")} <span style={{ fontSize: 11, color: "#333333", fontWeight: 500 }}>{t(lang, "cr_required_suffix")}</span></label>
              <label style={{ display: "block", border: `2px dashed ${hasPhoto ? "#0a0a0a" : "#E0E0E0"}`, borderRadius: 12, padding: 24, textAlign: "center", cursor: "pointer", backgroundColor: hasPhoto ? "#F5F5F5" : "#F8F8F8", marginBottom: 4 }}>
                <input type="file" accept="image/*" onChange={handlePhotoSelect} style={{ display: "none" }} />
                {photoUrl ? (
                  <img src={photoUrl} alt={t(lang, "cr_preview_alt")} style={{ maxHeight: 140, borderRadius: 8, marginBottom: 6 }} />
                ) : (
                  <div style={{ marginBottom: 6, display: "flex", justifyContent: "center" }}><CameraIcon size={26} color={hasPhoto ? "#0a0a0a" : "#999"} /></div>
                )}
                <div style={{ fontSize: 13, fontWeight: 600, color: hasPhoto ? "#0a0a0a" : "#999" }}>{uploading ? t(lang, "uploading_ellipsis") : hasPhoto ? t(lang, "cr_photo_added") : t(lang, "cr_add_photo")}</div>
              </label>
              {domain === "passion" && (
                <>
                  <label style={labelStyle}>{t(lang, "cr_age_range")}</label>
                  <AgeRangeField hasLimit={hasAgeLimit} onToggleLimit={toggleAgeLimit} minAge={minAge} maxAge={maxAge} onChangeMin={setMinAge} onChangeMax={setMaxAge} />
                </>
              )}
              <label style={labelStyle}>{t(lang, "bday_invite_friends_label")}</label>
              <FriendSelector selected={selectedFriends} onToggle={toggleFriend} />
            </div>
          )}

          {/* ÉTAPE 2 — MOVE : mode + titre + véhicule (Ride/Scoot uniquement) */}
          {step === 2 && domain === "move" && (
            <div>
              <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{t(lang, "cr_your_move_trip")}</h2>
              <p style={{ fontSize: 13, color: "#999", marginBottom: 20 }}>{t(lang, "cr_how_move")}</p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
                {MOVE_MODES.map(m => {
                  const Icon = MOVE_MODE_ICONS[m.key];
                  const active = moveMode === m.key;
                  return (
                    <button key={m.key} onClick={() => setMoveMode(m.key)} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: "16px 10px", borderRadius: 14, border: active ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0", backgroundColor: active ? "#0a0a0a" : "white", cursor: "pointer" }}>
                      <Icon size={22} color={active ? "white" : "#0a0a0a"} />
                      <span style={{ fontSize: 13, fontWeight: 700, color: active ? "white" : "#0a0a0a" }}>{t(lang, m.labelKey)}</span>
                    </button>
                  );
                })}
              </div>
              <label style={labelStyle}>{t(lang, "cr_title")}</label>
              <input style={inputStyle} value={title} onChange={e => setTitle(e.target.value)} placeholder={t(lang, "cr_ph_move_title")} maxLength={60} />
              <div style={{ fontSize: 11, color: "#999", textAlign: "right", marginBottom: 4 }}>{title.length}/60</div>
              {moveMode === "ride" && (
                <>
                  <label style={labelStyle}>{t(lang, "cr_vehicle_make")}</label>
                  <CarMakeField value={vehicleMake} onChange={setVehicleMake} placeholder={t(lang, "cr_ph_make_car")} />
                  <label style={labelStyle}>{t(lang, "cr_vehicle_model")}</label>
                  <input style={inputStyle} value={vehicleModel} onChange={e => setVehicleModel(e.target.value)} placeholder={t(lang, "cr_ph_model")} maxLength={60} />
                </>
              )}
            </div>
          )}

          {/* ÉTAPE 3 — MOVE : départ, destination, date, places, prix */}
          {step === 3 && domain === "move" && (
            <div>
              <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{t(lang, "cr_trip_and_seats")}</h2>
              <p style={{ fontSize: 13, color: "#999", marginBottom: 20 }}>{t(lang, "cr_move3_sub")}</p>

              <label style={labelStyle}>{t(lang, "cr_departure_place")}</label>
              <VenueField value={locationText} onChange={handleVenueChange} lang={lang} onNavigate={navigate} showSavedAddresses />

              <label style={labelStyle}>{t(lang, "cr_destination")}</label>
              <VenueField
                value={destinationText}
                onChange={(v, _id, lat, lng) => { setDestinationText(v); setDestinationLat(lat ?? null); setDestinationLng(lng ?? null); }}
                lang={lang}
                onNavigate={navigate}
              />

              <label style={labelStyle}>{t(lang, "cr_date")}</label>
              <input type="date" min={todayISO} style={inputStyle} value={selectedDate} onChange={e => handleDateChange(e.target.value)} />

              <label style={labelStyle}>{t(lang, "cr_departure_time")}</label>
              <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
                <select value={selectedHour} onChange={e => setSelectedHour(Number(e.target.value))} style={{ flex: 1, padding: "11px 14px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, backgroundColor: "#F8F8F8", outline: "none" }}>
                  {validHoursFor(selectedDate).map(i => <option key={i} value={i}>{String(i).padStart(2,"0")}h</option>)}
                </select>
                <select value={selectedMinute} onChange={e => setSelectedMinute(e.target.value)} style={{ flex: 1, padding: "11px 14px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, backgroundColor: "#F8F8F8", outline: "none" }}>
                  {validMinutesFor(selectedDate, selectedHour).map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>

              <label style={labelStyle}>{t(lang, "cr_seats_count")}</label>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <button onClick={() => setPlaces(p => String(Math.max(1, parseInt(p||"1") - 1)))} style={{ width: 44, height: 44, borderRadius: "50%", border: "1px solid #E0E0E0", backgroundColor: "white", fontSize: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>−</button>
                <div style={{ flex: 1, textAlign: "center" }}>
                  <span style={{ fontSize: 24, fontWeight: 800 }}>{places || "1"}</span>
                  <span style={{ fontSize: 13, color: "#999", marginLeft: 6 }}>{t(lang, "places")}</span>
                </div>
                <button onClick={() => setPlaces(p => String(parseInt(p||"1") + 1))} style={{ width: 44, height: 44, borderRadius: "50%", border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>+</button>
              </div>

              <label style={labelStyle}>{t(lang, "cr_price_per_seat")}</label>
              <Toggle value={isFree} onChange={setIsFree} label={t(lang, "price_free")} />
              {!isFree && (
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, backgroundColor: "#F8F8F8", borderRadius: 14, padding: "8px 16px", border: "1px solid #E0E0E0" }}>
                  <button onClick={() => setPrice(p => String(Math.max(0, parseInt(p||"0") - 1)))} style={{ width: 44, height: 44, borderRadius: "50%", border: "1px solid #E0E0E0", backgroundColor: "white", fontSize: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>−</button>
                  <div style={{ flex: 1, textAlign: "center" }}>
                    <span style={{ fontSize: 28, fontWeight: 800 }}>{price || "0"}</span>
                    <span style={{ fontSize: 16, color: "#999", marginLeft: 4 }}>{t(lang, "cr_per_seat_unit")}</span>
                  </div>
                  <button onClick={() => setPrice(p => String(parseInt(p||"0") + 1))} style={{ width: 44, height: 44, borderRadius: "50%", border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>+</button>
                </div>
              )}
            </div>
          )}

          {step === 2 && domain === "ritual" && (
            <div>
              <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{t(lang, "cr_your_ritual")}</h2>
              <p style={{ fontSize: 13, color: "#999", marginBottom: 20 }}>{t(lang, "cr_ritual_sub")}</p>
              <label style={labelStyle}>{t(lang, "cr_category")}</label>
              <CategoryDropdown cats={getCats()} groups={getGroups()} selected={category} onSelect={setCategory} placeholder={t(lang, "cr_choose_activity")} lang={lang} />
              <label style={labelStyle}>{t(lang, "cr_ritual_name")}</label>
              <input style={inputStyle} value={title} onChange={e => setTitle(e.target.value)} placeholder={t(lang, "cr_ph_ritual_name")} maxLength={60} />
              <label style={labelStyle}>{t(lang, "cr_description")}</label>
              <textarea style={{ ...inputStyle, minHeight: 80, resize: "none" } as any} value={description} onChange={e => setDescription(e.target.value)} placeholder={t(lang, "cr_ph_desc_ritual")} maxLength={200} />
              <label style={labelStyle}>{t(lang, "cr_frequency")}</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
                {FREQUENCES.map(f => (
                  <div key={f.key} onClick={() => setFrequence(f.key)} style={{ padding: "8px 14px", borderRadius: 20, border: `1px solid ${frequence === f.key ? "#0a0a0a" : "#E0E0E0"}`, backgroundColor: frequence === f.key ? "#0a0a0a" : "#F8F8F8", cursor: "pointer", fontSize: 13, color: frequence === f.key ? "white" : "#666", fontWeight: frequence === f.key ? 600 : 400 }}>{t(lang, f.labelKey)}</div>
                ))}
              </div>
              {(frequence === "weekly" || frequence === "custom") && (
                <>
                  <label style={labelStyle}>{t(lang, "cr_days")}</label>
                  <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                    {JOURS.map(jour => (
                      <div key={jour.v} onClick={() => toggleDay(jour.v)} style={{ width: 40, height: 40, borderRadius: "50%", border: `1px solid ${selectedDays.includes(jour.v) ? "#0a0a0a" : "#E0E0E0"}`, backgroundColor: selectedDays.includes(jour.v) ? "#0a0a0a" : "#F8F8F8", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 11, color: selectedDays.includes(jour.v) ? "white" : "#666", fontWeight: 600 }}>{t(lang, jour.k)}</div>
                    ))}
                  </div>
                </>
              )}
              <label style={labelStyle}>{t(lang, "cr_cover_photo")} <span style={{ fontSize: 11, color: "#333333", fontWeight: 500 }}>{t(lang, "cr_required_suffix")}</span></label>
              <label style={{ display: "block", border: `2px dashed ${hasPhoto ? "#0a0a0a" : "#E0E0E0"}`, borderRadius: 12, padding: 24, textAlign: "center", cursor: "pointer", backgroundColor: hasPhoto ? "#F5F5F5" : "#F8F8F8", marginBottom: 4 }}>
                <input type="file" accept="image/*" onChange={handlePhotoSelect} style={{ display: "none" }} />
                {photoUrl ? (
                  <img src={photoUrl} alt={t(lang, "cr_preview_alt")} style={{ maxHeight: 140, borderRadius: 8, marginBottom: 6 }} />
                ) : (
                  <div style={{ marginBottom: 6, display: "flex", justifyContent: "center" }}><CameraIcon size={26} color={hasPhoto ? "#0a0a0a" : "#999"} /></div>
                )}
                <div style={{ fontSize: 13, fontWeight: 600, color: hasPhoto ? "#0a0a0a" : "#999" }}>{uploading ? t(lang, "uploading_ellipsis") : hasPhoto ? t(lang, "cr_photo_added") : t(lang, "cr_add_photo")}</div>
              </label>
              <label style={labelStyle}>{t(lang, "cr_age_range")}</label>
              <AgeRangeField hasLimit={hasAgeLimit} onToggleLimit={toggleAgeLimit} minAge={minAge} maxAge={maxAge} onChangeMin={setMinAge} onChangeMax={setMaxAge} />
              <label style={labelStyle}>{t(lang, "bday_invite_friends_label")}</label>
              <FriendSelector selected={selectedFriends} onToggle={toggleFriend} />
              <label style={labelStyle}>{t(lang, "cr_visibility")}</label>
              <AudienceSelector value={audience} onChange={setAudience} options={audienceOpts} />
            </div>
          )}

          {step === 2 && domain === "community" && (
            <div>
              <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{t(lang, "cr_your_community")}</h2>
              <label style={labelStyle}>{t(lang, "cr_category")}</label>
              <CategoryDropdown cats={getCats()} selected={category} onSelect={setCategory} placeholder={t(lang, "cr_choose_category")} lang={lang} />
              <label style={labelStyle}>{t(lang, "cr_community_name")}</label>
              <input style={inputStyle} value={communityName} onChange={e => setCommunityName(e.target.value)} placeholder={t(lang, "cr_ph_community_name")} maxLength={50} />
              <label style={labelStyle}>{t(lang, "cr_description")}</label>
              <textarea style={{ ...inputStyle, minHeight: 100, resize: "none" } as any} value={description} onChange={e => setDescription(e.target.value)} placeholder={t(lang, "cr_ph_desc_community")} maxLength={300} />
              <label style={labelStyle}>{t(lang, "cr_cover_photo")} <span style={{ fontSize: 11, color: "#333333", fontWeight: 500 }}>{t(lang, "cr_required_suffix")}</span></label>
              <label style={{ display: "block", border: `2px dashed ${hasPhoto ? "#0a0a0a" : "#E0E0E0"}`, borderRadius: 12, padding: 24, textAlign: "center", cursor: "pointer", backgroundColor: hasPhoto ? "#F5F5F5" : "#F8F8F8", marginBottom: 4 }}>
                <input type="file" accept="image/*" onChange={handlePhotoSelect} style={{ display: "none" }} />
                {photoUrl ? (
                  <img src={photoUrl} alt={t(lang, "cr_preview_alt")} style={{ maxHeight: 140, borderRadius: 8, marginBottom: 6 }} />
                ) : (
                  <div style={{ marginBottom: 6, display: "flex", justifyContent: "center" }}><CameraIcon size={26} color={hasPhoto ? "#0a0a0a" : "#999"} /></div>
                )}
                <div style={{ fontSize: 13, fontWeight: 600, color: hasPhoto ? "#0a0a0a" : "#999" }}>{uploading ? t(lang, "uploading_ellipsis") : hasPhoto ? t(lang, "cr_photo_added") : t(lang, "cr_add_photo")}</div>
              </label>
              <label style={labelStyle}>{t(lang, "cr_age_range")}</label>
              <AgeRangeField hasLimit={hasAgeLimit} onToggleLimit={toggleAgeLimit} minAge={minAge} maxAge={maxAge} onChangeMin={setMinAge} onChangeMax={setMaxAge} />
              <label style={labelStyle}>{t(lang, "bday_invite_friends_label")}</label>
              <FriendSelector selected={selectedFriends} onToggle={toggleFriend} />
              <label style={labelStyle}>{t(lang, "cr_membership")}</label>
              <AudienceSelector value={audience} onChange={setAudience} options={audienceOpts} />
            </div>
          )}

          {step === 2 && domain === "pro_event" && (
            <div>
              <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{t(lang, "cr_your_pro_event")}</h2>
              <p style={{ fontSize: 13, color: "#999", marginBottom: 20 }}>{t(lang, "cr_pro_event_sub").replace("{name}", selectedIdentity.name || "")}</p>
              <label style={labelStyle}>{t(lang, "cr_category")}</label>
              <CategoryDropdown cats={getCats()} groups={getGroups()} selected={category} onSelect={setCategory} placeholder={t(lang, "cr_choose_event_type")} lang={lang} />
              <label style={labelStyle}>{t(lang, "cr_title")}</label>
              <input style={inputStyle} value={title} onChange={e => setTitle(e.target.value)} placeholder={t(lang, "cr_ph_pro_event_title")} maxLength={60} />
              <label style={labelStyle}>{t(lang, "cr_description")}</label>
              <textarea style={{ ...inputStyle, minHeight: 100, resize: "none" } as any} value={description} onChange={e => setDescription(e.target.value)} placeholder={t(lang, "cr_ph_desc_pro_event")} maxLength={300} />
              <label style={labelStyle}>{t(lang, "cr_cover_photo")} <span style={{ fontSize: 11, color: "#333333", fontWeight: 500 }}>{t(lang, "cr_required_suffix")}</span></label>
              <label style={{ display: "block", border: `2px dashed ${hasPhoto ? "#0a0a0a" : "#E0E0E0"}`, borderRadius: 12, padding: 24, textAlign: "center", cursor: "pointer", backgroundColor: hasPhoto ? "#F5F5F5" : "#F8F8F8", marginBottom: 4 }}>
                <input type="file" accept="image/*" onChange={handlePhotoSelect} style={{ display: "none" }} />
                {photoUrl ? (
                  <img src={photoUrl} alt={t(lang, "cr_preview_alt")} style={{ maxHeight: 140, borderRadius: 8, marginBottom: 6 }} />
                ) : (
                  <div style={{ marginBottom: 6, display: "flex", justifyContent: "center" }}><CameraIcon size={26} color={hasPhoto ? "#0a0a0a" : "#999"} /></div>
                )}
                <div style={{ fontSize: 13, fontWeight: 600, color: hasPhoto ? "#0a0a0a" : "#999" }}>{uploading ? t(lang, "uploading_ellipsis") : hasPhoto ? t(lang, "cr_photo_added") : t(lang, "cr_add_photo")}</div>
              </label>

              {/* Jamais les amis personnels — on invite parmi les membres et abonnés de CE lieu
                  (voir VenuePeopleSelector ci-dessus et le pendant serveur inviteVenuePeople). */}
              <label style={labelStyle}>{t(lang, "cr_invite_members_followers")}</label>
              {selectedIdentity.id && <VenuePeopleSelector venueId={selectedIdentity.id} selected={selectedFriends} onToggle={toggleFriend} onToggleMany={toggleFriendsMany} />}

              {/* Co-organisation : le lieu invité doit accepter (notification) avant que l'événement
                  n'apparaisse sur SA propre page — voir POST /publications/:id/collaborators. */}
              <label style={labelStyle}>{t(lang, "cr_partner_venues")}</label>
              <p style={{ fontSize: 12.5, color: "#999", marginTop: -4, marginBottom: 8 }}>
                {t(lang, "cr_partner_venues_hint")}
              </p>
              {selectedIdentity.id && <PartnerVenuesField excludeVenueId={selectedIdentity.id} selected={partnerVenues} onChange={setPartnerVenues} />}
            </div>
          )}

          {step === 2 && domain === "feedback" && (
            <div>
              <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{t(lang, "cr_your_feedback")}</h2>
              <p style={{ fontSize: 13, color: "#999", marginBottom: 20 }}>{t(lang, "cr_feedback_sub")}</p>

              <label style={labelStyle}>{t(lang, "cr_lived_activity")} <span style={{ fontSize: 11, color: "#333333" }}>{t(lang, "cr_required_suffix")}</span></label>
              <FeedbackSourcePicker
                value={feedbackRefType ? { type: feedbackRefType, id: feedbackRefId } : null}
                onSelect={(type, id, label, cat) => { setFeedbackRefType(type); setFeedbackRefId(id); setTitle(label); if (cat) setCategory(cat); setMentionedUserIds([]); }}
              />

              {feedbackRefType && feedbackRefId && (
                <>
                  <label style={labelStyle}>{t(lang, "cr_mention_people")}</label>
                  <MentionPicker refType={feedbackRefType} refId={feedbackRefId} selected={mentionedUserIds} onToggle={id => setMentionedUserIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])} />
                </>
              )}

              <label style={labelStyle}>{t(lang, "cr_description")} <span style={{ fontSize: 11, color: "#333333" }}>{t(lang, "cr_required_suffix")}</span></label>
              <textarea style={{ ...inputStyle, minHeight: 80, resize: "none" } as any} value={description} onChange={e => setDescription(e.target.value)} placeholder={t(lang, "cr_ph_desc_feedback")} />

              <label style={labelStyle}>{t(lang, "cr_memory_media")} <span style={{ fontSize: 11, color: "#333333" }}>{t(lang, "cr_required_suffix")}</span></label>
              <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 8, marginBottom: 4 }}>
                {feedbackMedia.map(m => (
                  <div key={m.url} style={{ position: "relative", width: 76, height: 76, borderRadius: 10, overflow: "hidden", backgroundColor: "#000" }}>
                    {m.media_type === "video" ? (
                      <video src={m.url} style={{ width: "100%", height: "100%", objectFit: "cover" }} muted />
                    ) : (
                      <img src={m.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    )}
                    <button type="button" onClick={() => removeFeedbackMedia(m.url)} style={{ position: "absolute", top: 3, right: 3, width: 20, height: 20, borderRadius: "50%", border: "none", backgroundColor: "rgba(0,0,0,0.65)", color: "white", fontSize: 12, lineHeight: "20px", cursor: "pointer", padding: 0 }}>✕</button>
                  </div>
                ))}
                {feedbackMedia.length < MAX_FEEDBACK_MEDIA && (
                  <label style={{ width: 76, height: 76, borderRadius: 10, border: "2px dashed #E0E0E0", backgroundColor: "#F8F8F8", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexDirection: "column" as const, gap: 2 }}>
                    <input type="file" accept="image/*,video/*" multiple onChange={handleFeedbackMediaSelect} style={{ display: "none" }} />
                    <span style={{ fontSize: 20, color: "#999" }}>+</span>
                    <span style={{ fontSize: 9, color: "#999" }}>{uploadingFeedbackMedia ? t(lang, "cr_uploading_short") : t(lang, "add")}</span>
                  </label>
                )}
              </div>
              <div style={{ fontSize: 11, color: "#999", marginBottom: 4 }}>{feedbackMedia.length}/{MAX_FEEDBACK_MEDIA} {t(lang, "cr_media_hint")}</div>

              <label style={labelStyle}>{t(lang, "cr_audience")}</label>
              <AudienceSelector value={audience} onChange={setAudience} options={feedbackAudienceOpts} />
            </div>
          )}

          {/* ÉTAPE 3 — Lieu & Date (passion/power/pro_event) */}
          {step === 3 && (domain === "passion" || domain === "power" || domain === "pro_event") && (
            <div>
              <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{t(lang, "cr_place_and_date")}</h2>
              <p style={{ fontSize: 13, color: "#999", marginBottom: 20 }}>
                {selectedIdentity.type === "venue" ? t(lang, "cr_venue_default_hint").replace("{name}", selectedIdentity.name || "") : t(lang, "cr_where_when")}
              </p>

              {domain === "power" && (
                <>
                  <label style={labelStyle}>{t(lang, "cr_where_service")}</label>
                  <div style={{ display: "flex", gap: 6, marginBottom: 4, flexWrap: "wrap" as const }}>
                    {([
                      { key: "home", label: t(lang, "cr_at_client") },
                      { key: "provider", label: t(lang, "cr_at_mine") },
                      { key: "remote", label: t(lang, "cr_at_remote") },
                    ] as const).map(o => (
                      <button key={o.key} onClick={() => setLocationType(o.key)} style={{ padding: "8px 14px", borderRadius: 20, fontSize: 12.5, fontWeight: 600, cursor: "pointer", border: locationType === o.key ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0", backgroundColor: locationType === o.key ? "#0a0a0a" : "white", color: locationType === o.key ? "white" : "#666" }}>
                        {o.label}
                      </button>
                    ))}
                  </div>
                  <div style={{ fontSize: 11.5, color: "#999", marginBottom: 16 }}>
                    {locationType === "provider" ? t(lang, "cr_hint_provider") : locationType === "remote" ? t(lang, "cr_hint_remote") : t(lang, "cr_hint_client")}
                  </div>
                </>
              )}

              {(domain === "passion" || domain === "pro_event" || (domain === "power" && locationType === "provider")) && (
                <>
                  <label style={labelStyle}>{domain === "pro_event" ? t(lang, "cr_place_or_address") : t(lang, "publish_as_type_venue")}</label>
                  <VenueField value={locationText} onChange={handleVenueChange} lang={lang} onNavigate={navigate} showSavedAddresses />
                </>
              )}

              <label style={labelStyle}>{t(lang, "cr_date")}</label>
              <input type="date" min={todayISO} style={inputStyle} value={selectedDate} onChange={e => handleDateChange(e.target.value)} />

              <label style={labelStyle}>{t(lang, "cr_time")}</label>
              <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
                <select value={selectedHour} onChange={e => setSelectedHour(Number(e.target.value))} style={{ flex: 1, padding: "11px 14px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, backgroundColor: "#F8F8F8", outline: "none" }}>
                  {validHoursFor(selectedDate).map(i => <option key={i} value={i}>{String(i).padStart(2,"0")}h</option>)}
                </select>
                <select value={selectedMinute} onChange={e => setSelectedMinute(e.target.value)} style={{ flex: 1, padding: "11px 14px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, backgroundColor: "#F8F8F8", outline: "none" }}>
                  {validMinutesFor(selectedDate, selectedHour).map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>

              <label style={labelStyle}>{t(lang, "cr_est_duration")}</label>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <button onClick={() => setDuration((d: number) => Math.max(0.5, d - 0.5))} style={{ width: 44, height: 44, borderRadius: "50%", border: "1px solid #E0E0E0", backgroundColor: "white", fontSize: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>−</button>
                <div style={{ flex: 1, textAlign: "center", fontSize: 18, fontWeight: 700 }}>
                  {duration < 1 ? t(lang, "cr_dur_30min") : duration === 1 ? "1h" : duration % 1 === 0.5 ? `${Math.floor(duration)}h30` : `${duration}h`}
                </div>
                <button onClick={() => setDuration((d: number) => Math.min(12, d + 0.5))} style={{ width: 44, height: 44, borderRadius: "50%", border: "1px solid #E0E0E0", backgroundColor: "white", fontSize: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>+</button>
              </div>

              <label style={labelStyle}>{t(lang, "cr_price_per_person")}</label>
              <Toggle value={isFree} onChange={setIsFree} label={t(lang, "price_free")} />
              {!isFree && (
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, backgroundColor: "#F8F8F8", borderRadius: 14, padding: "8px 16px", border: "1px solid #E0E0E0" }}>
                  <button onClick={() => setPrice(p => String(Math.max(0, parseInt(p||"0") - 1)))} style={{ width: 44, height: 44, borderRadius: "50%", border: "1px solid #E0E0E0", backgroundColor: "white", fontSize: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>−</button>
                  <div style={{ flex: 1, textAlign: "center" }}>
                    <span style={{ fontSize: 28, fontWeight: 800 }}>{price || "0"}</span>
                    <span style={{ fontSize: 16, color: "#999", marginLeft: 4 }}>{t(lang, "cr_per_person_unit")}</span>
                  </div>
                  <button onClick={() => setPrice(p => String(parseInt(p||"0") + 1))} style={{ width: 44, height: 44, borderRadius: "50%", border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>+</button>
                </div>
              )}

              <label style={labelStyle}>{t(lang, "cr_seats_count")}</label>
              <Toggle value={!hasPlacesLimit} onChange={v => toggleHasPlacesLimit(!v)} label={t(lang, "cr_unlimited")} />
              {hasPlacesLimit && (
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 10, marginBottom: 16 }}>
                  <button onClick={() => setPlaces(p => String(Math.max(2, parseInt(p||"2") - 1)))} style={{ width: 44, height: 44, borderRadius: "50%", border: "1px solid #E0E0E0", backgroundColor: "white", fontSize: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>−</button>
                  <div style={{ flex: 1, textAlign: "center" }}>
                    <span style={{ fontSize: 24, fontWeight: 800 }}>{places || "2"}</span>
                    <span style={{ fontSize: 13, color: "#999", marginLeft: 6 }}>{t(lang, "places")}</span>
                  </div>
                  <button onClick={() => setPlaces(p => String(parseInt(p||"1") + 1))} style={{ width: 44, height: 44, borderRadius: "50%", border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>+</button>
                </div>
              )}
            </div>
          )}

          {/* ÉTAPE 3 — Lieu ritual */}
          {step === 3 && domain === "ritual" && (
            <div>
              <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{t(lang, "cr_usual_place")}</h2>
              <p style={{ fontSize: 13, color: "#999", marginBottom: 20 }}>{t(lang, "cr_usual_place_sub")}</p>
              <label style={labelStyle}>{t(lang, "publish_as_type_venue")}</label>
              <VenueField value={locationText} onChange={handleVenueChange} lang={lang} onNavigate={navigate} showSavedAddresses />
            </div>
          )}

          {/* ÉTAPE 4 — Audience */}
          {step === 4 && (domain === "passion" || domain === "power" || domain === "pro_event") && (
            <div>
              <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{domain === "pro_event" ? t(lang, "cr_registrations") : t(lang, "cr_audience_privacy")}</h2>
              {domain === "pro_event" ? (
                <p style={{ fontSize: 13, color: "#999", marginBottom: 20 }}>{t(lang, "cr_pro_event_step_sub").replace("{name}", selectedIdentity.name || "")}</p>
              ) : (
                <>
                  <p style={{ fontSize: 13, color: "#999", marginBottom: 20 }}>{t(lang, "cr_who_can_join")}</p>
                  <AudienceSelector value={audience} onChange={setAudience} options={audienceOpts} />
                </>
              )}
              <Toggle value={needApproval} onChange={setNeedApproval} label={t(lang, "cr_approval_required")} sub={t(lang, "cr_approval_required_sub")} />
              <Toggle value={waitlistEnabled} onChange={setWaitlistEnabled} label={t(lang, "cr_waitlist")} sub={t(lang, "cr_waitlist_sub")} />
              <label style={labelStyle}>{t(lang, "cr_min_score")}</label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
                {[0,5,6,7,8,9].map(score => (
                  <div key={score} onClick={() => setMinScore(score)} style={{ padding: "7px 14px", borderRadius: 20, border: `1px solid ${minScore === score ? "#0a0a0a" : "#E0E0E0"}`, backgroundColor: minScore === score ? "#0a0a0a" : "#F8F8F8", cursor: "pointer", fontSize: 12, color: minScore === score ? "white" : "#666", fontWeight: minScore === score ? 600 : 400 }}>
                    {score === 0 ? t(lang, "cr_none") : `≥ ${score}`}
                  </div>
                ))}
              </div>

              {/* Récap */}
              <div style={{ backgroundColor: "#F8F8F8", borderRadius: 14, padding: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>{t(lang, "cr_recap")}</div>
                {[
                  { key: t(lang, "cr_type"), value: domain.toUpperCase() },
                  { key: t(lang, "cr_category"), value: category },
                  { key: t(lang, "cr_title"), value: title },
                  { key: t(lang, "publish_as_type_venue"), value: locationText || "-" },
                  { key: t(lang, "cr_date"), value: selectedDate ? `${selectedDate} ${t(lang, "cr_at_time")} ${String(selectedHour).padStart(2,"0")}:${selectedMinute}` : "-" },
                  { key: t(lang, "cr_price"), value: isFree ? t(lang, "price_free") : price ? t(lang, "cr_price_per_pers_short").replace("{price}", price) : "-" },
                  { key: t(lang, "cr_places_label"), value: places || t(lang, "cr_unlimited") },
                  { key: t(lang, "friends"), value: selectedFriends.length > 0 ? t(lang, "cr_friends_count").replace("{n}", String(selectedFriends.length)) : t(lang, "cr_none") },
                ].map(row => (
                  <div key={row.key} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #F0F0F0" }}>
                    <span style={{ fontSize: 12, color: "#999" }}>{row.key}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: "#0a0a0a", maxWidth: "60%", textAlign: "right" }}>{row.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div style={{ backgroundColor: "white", borderTop: "1px solid #E0E0E0", padding: 16, flexShrink: 0, position: "sticky", bottom: 0, zIndex: 50 }}>
        <div style={{ maxWidth: 700, margin: "0 auto" }}>
          <button onClick={() => { if (canNext()) { if (step < totalSteps) setStep(step + 1); else {
        handleSubmit();
      } } }} disabled={!canNext()} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, backgroundColor: canNext() ? "#0a0a0a" : "#E0E0E0", color: "white", border: "none", borderRadius: 14, padding: "15px", fontSize: 15, fontWeight: 700, cursor: canNext() ? "pointer" : "not-allowed" }}>
            {step === totalSteps ? t(lang, "publish") : t(lang, "cr_next")} →
          </button>
        </div>
      </div>

      {showLive && (
        <LiveRecordModal onClose={() => setShowLive(false)} onCreated={() => { setShowLive(false); navigate("/profile"); }} />
      )}

      {coverCropFile && (
        <ImageCropModal
          file={coverCropFile}
          aspect={16 / 9}
          shape="rect"
          title={t(lang, "cr_crop_cover_title") || "Recadrer la couverture"}
          onCancel={() => setCoverCropFile(null)}
          onConfirm={confirmCoverCrop}
        />
      )}
    </div>
  );
}
